import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { 
  Calendar, RefreshCw, Save, 
  Plus, Trash2, ChevronDown, ChevronUp, Edit2, X, Check
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminContent = () => {
  const navigate = useNavigate();
  const [programContent, setProgramContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchContent();
  }, [navigate]);

  const fetchContent = async () => {
    try {
      setLoading(true);
      const programRes = await fetch(`${API_URL}/api/content/program`);
      
      if (programRes.ok) {
        setProgramContent(await programRes.json());
      }
    } catch (err) {
      console.error('Error fetching content:', err);
      toast.error('Failed to load content');
    } finally {
      setLoading(false);
    }
  };

  const seedContent = async () => {
    try {
      const response = await fetch(`${API_URL}/api/content/program/seed`, {
        method: 'POST'
      });
      if (response.ok) {
        toast.success('Program content seeded successfully');
        fetchContent();
      }
    } catch (err) {
      toast.error('Failed to seed program content');
    }
  };

  const saveContent = async (data) => {
    try {
      setSaving(true);
      const response = await fetch(`${API_URL}/api/content/program`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });
      
      if (response.ok) {
        toast.success('Content saved successfully');
        fetchContent();
      } else {
        throw new Error('Failed to save');
      }
    } catch (err) {
      toast.error('Failed to save content');
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminLayout>
      <div className="p-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Program Content</h1>
            <p className="text-gray-600">Manage basic program page content (for advanced editing, use the Program CMS)</p>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={seedContent}
              variant="outline"
              className="flex items-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Seed Program Data
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-12 h-12 border-4 border-[#00457f] border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : (
          programContent && (
            <ProgramEditor 
              content={programContent} 
              onSave={saveContent}
              saving={saving}
            />
          )
        )}
      </div>
    </AdminLayout>
  );
};

// Program Content Editor
const ProgramEditor = ({ content, onSave, saving }) => {
  const [editedContent, setEditedContent] = useState(content);
  const [expandedDays, setExpandedDays] = useState({});
  const [editingSession, setEditingSession] = useState(null);

  useEffect(() => {
    setEditedContent(content);
  }, [content]);

  const handleSave = () => {
    onSave(editedContent);
  };

  const toggleDay = (dayNum) => {
    setExpandedDays(prev => ({ ...prev, [dayNum]: !prev[dayNum] }));
  };

  const deleteSession = (dayIndex, sessionIndex) => {
    if (!window.confirm('Delete this session?')) return;
    const newDays = [...editedContent.days];
    newDays[dayIndex].sessions.splice(sessionIndex, 1);
    setEditedContent({ ...editedContent, days: newDays });
  };

  const addSession = (dayIndex) => {
    const newDays = [...editedContent.days];
    const newSession = {
      id: `new-${Date.now()}`,
      title: 'New Session',
      description: '',
      start_time: '',
      end_time: '',
      session_type: 'panel',
      speakers: [],
      location: '',
      day: dayIndex + 1,
      order: newDays[dayIndex].sessions.length
    };
    newDays[dayIndex].sessions.push(newSession);
    setEditedContent({ ...editedContent, days: newDays });
  };

  return (
    <div className="space-y-6">
      {/* Hero Settings */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4">Hero Section</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
            <input
              type="text"
              value={editedContent.hero_title}
              onChange={(e) => setEditedContent({ ...editedContent, hero_title: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#00457f]"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Subtitle</label>
            <input
              type="text"
              value={editedContent.hero_subtitle}
              onChange={(e) => setEditedContent({ ...editedContent, hero_subtitle: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#00457f]"
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Preliminary Notice</label>
            <textarea
              value={editedContent.preliminary_notice || ''}
              onChange={(e) => setEditedContent({ ...editedContent, preliminary_notice: e.target.value })}
              rows={2}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#00457f]"
              placeholder="Optional notice about the preliminary nature of the program..."
            />
          </div>
        </div>
      </div>

      {/* Program Days */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4">Program Schedule</h3>
        
        {editedContent.days && editedContent.days.map((day, dayIndex) => (
          <div key={day.day_number} className="mb-6 border rounded-lg overflow-hidden">
            <button
              onClick={() => toggleDay(day.day_number)}
              className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors"
            >
              <div className="flex items-center gap-3">
                <Calendar className="w-5 h-5 text-[#00457f]" />
                <div className="text-left">
                  <h4 className="font-bold text-gray-900">{day.title}</h4>
                  <p className="text-sm text-gray-500">{day.date} • {day.sessions?.length || 0} sessions</p>
                </div>
              </div>
              {expandedDays[day.day_number] ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
            </button>
            
            {expandedDays[day.day_number] && (
              <div className="p-4 space-y-3">
                {day.sessions?.map((session, sessionIndex) => (
                  <div 
                    key={session.id} 
                    className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-medium text-[#00457f] bg-blue-50 px-2 py-0.5 rounded">
                          {session.start_time} {session.end_time && `- ${session.end_time}`}
                        </span>
                        <span className="text-xs text-gray-500 bg-gray-200 px-2 py-0.5 rounded capitalize">
                          {session.session_type}
                        </span>
                      </div>
                      <h5 className="font-medium text-gray-900">{session.title}</h5>
                      {session.description && (
                        <p className="text-sm text-gray-600 mt-1">{session.description}</p>
                      )}
                      {session.speakers && session.speakers.length > 0 && (
                        <p className="text-xs text-gray-500 mt-1">
                          Speakers: {session.speakers.join(', ')}
                        </p>
                      )}
                    </div>
                    <div className="flex gap-1">
                      <button
                        onClick={() => setEditingSession({ dayIndex, sessionIndex, session })}
                        className="p-1.5 text-gray-400 hover:text-[#00457f] hover:bg-blue-50 rounded"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => deleteSession(dayIndex, sessionIndex)}
                        className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => addSession(dayIndex)}
                  className="mt-2"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add Session
                </Button>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Logistics */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4">Logistics & Travel</h3>
        <div className="space-y-3">
          {editedContent.logistics?.map((item, index) => (
            <div key={item.id} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
              <div className="flex-1">
                <input
                  type="text"
                  value={item.title}
                  onChange={(e) => {
                    const newLogistics = [...editedContent.logistics];
                    newLogistics[index].title = e.target.value;
                    setEditedContent({ ...editedContent, logistics: newLogistics });
                  }}
                  className="w-full font-medium text-gray-900 bg-transparent border-b border-transparent hover:border-gray-300 focus:border-[#00457f] focus:outline-none"
                />
                <textarea
                  value={item.content}
                  onChange={(e) => {
                    const newLogistics = [...editedContent.logistics];
                    newLogistics[index].content = e.target.value;
                    setEditedContent({ ...editedContent, logistics: newLogistics });
                  }}
                  rows={2}
                  className="w-full mt-1 text-sm text-gray-600 bg-transparent border-b border-transparent hover:border-gray-300 focus:border-[#00457f] focus:outline-none resize-none"
                />
              </div>
              <button
                onClick={() => {
                  const newLogistics = editedContent.logistics.filter((_, i) => i !== index);
                  setEditedContent({ ...editedContent, logistics: newLogistics });
                }}
                className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
          
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              const newLogistics = [...(editedContent.logistics || []), {
                id: `new-${Date.now()}`,
                title: 'New Section',
                content: '',
                order: editedContent.logistics?.length || 0
              }];
              setEditedContent({ ...editedContent, logistics: newLogistics });
            }}
          >
            <Plus className="w-4 h-4 mr-1" />
            Add Logistics Section
          </Button>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button 
          onClick={handleSave}
          disabled={saving}
          className="bg-[#00457f] hover:bg-[#003461]"
        >
          {saving ? (
            <>
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              Save Program Content
            </>
          )}
        </Button>
      </div>

      {/* Edit Session Modal */}
      {editingSession && (
        <SessionEditModal
          session={editingSession.session}
          onSave={(updatedSession) => {
            const newDays = [...editedContent.days];
            newDays[editingSession.dayIndex].sessions[editingSession.sessionIndex] = updatedSession;
            setEditedContent({ ...editedContent, days: newDays });
            setEditingSession(null);
          }}
          onClose={() => setEditingSession(null)}
        />
      )}
    </div>
  );
};

// Session Edit Modal
const SessionEditModal = ({ session, onSave, onClose }) => {
  const [editedSession, setEditedSession] = useState(session);

  const sessionTypes = ['keynote', 'panel', 'ceremony', 'networking', 'tour', 'reception', 'break', 'lunch'];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-bold text-[#00457f]">Edit Session</h2>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
              <X className="w-5 h-5" />
            </button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
              <input
                type="text"
                value={editedSession.title}
                onChange={(e) => setEditedSession({ ...editedSession, title: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#00457f]"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Start Time</label>
              <input
                type="text"
                value={editedSession.start_time}
                onChange={(e) => setEditedSession({ ...editedSession, start_time: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#00457f]"
                placeholder="e.g., 9:00 AM"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">End Time</label>
              <input
                type="text"
                value={editedSession.end_time}
                onChange={(e) => setEditedSession({ ...editedSession, end_time: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#00457f]"
                placeholder="e.g., 10:00 AM"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Session Type</label>
              <select
                value={editedSession.session_type}
                onChange={(e) => setEditedSession({ ...editedSession, session_type: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#00457f]"
              >
                {sessionTypes.map(type => (
                  <option key={type} value={type} className="capitalize">{type}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
              <input
                type="text"
                value={editedSession.location || ''}
                onChange={(e) => setEditedSession({ ...editedSession, location: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#00457f]"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea
                value={editedSession.description || ''}
                onChange={(e) => setEditedSession({ ...editedSession, description: e.target.value })}
                rows={3}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#00457f]"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Speakers (comma-separated)</label>
              <input
                type="text"
                value={editedSession.speakers?.join(', ') || ''}
                onChange={(e) => setEditedSession({ ...editedSession, speakers: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#00457f]"
                placeholder="Speaker 1, Speaker 2, ..."
              />
            </div>
          </div>
          
          <div className="flex justify-end gap-2 mt-6">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={() => onSave(editedSession)} className="bg-[#00457f] hover:bg-[#003461]">
              <Check className="w-4 h-4 mr-2" />
              Save Session
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminContent;
